import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MergeFieldInserter } from '@/components/messaging/merge-field-inserter';
import { FormField } from '@/components/shared/form-field';
import { FundSelect } from '@/components/shared/fund-select';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { insertAtSelection } from '@/lib/messaging-merge-fields';
import type { ReceiptSmsFundOverride } from '@/lib/giving-default-funds';

interface ReceiptSmsOverrideDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  override?: ReceiptSmsFundOverride | null;
  onSave: (override: ReceiptSmsFundOverride) => void;
  onDelete?: () => void;
  disabled?: boolean;
  popoverPortal?: 'dialog' | 'body';
}

export const ReceiptSmsOverrideDialog = ({
  open,
  onOpenChange,
  override = null,
  onSave,
  onDelete,
  disabled = false,
  popoverPortal = 'dialog',
}: ReceiptSmsOverrideDialogProps) => {
  const { t } = useTranslation('giving');
  const { t: tCommon } = useTranslation();

  const [fundId, setFundId] = useState('');
  const [body, setBody] = useState('');
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [selection, setSelection] = useState({ start: 0, end: 0 });

  const isEditing = onDelete != null;

  useEffect(() => {
    if (!open) return;
    setFundId(override?.fundId ?? '');
    setBody(override?.body ?? '');
  }, [open, override]);

  const rememberSelection = () => {
    const el = bodyRef.current;
    if (!el) return;
    setSelection({
      start: el.selectionStart ?? body.length,
      end: el.selectionEnd ?? body.length,
    });
  };

  const insertIntoBody = (token: string) => {
    const { value: next, caret } = insertAtSelection(body, selection.start, selection.end, token);
    setBody(next);
    setSelection({ start: caret, end: caret });
    requestAnimationFrame(() => {
      bodyRef.current?.focus();
      bodyRef.current?.setSelectionRange(caret, caret);
    });
  };

  const canSave = fundId.trim() !== '' && body.trim() !== '';

  const handleSave = () => {
    if (!canSave) return;
    onSave({ fundId, body: body.trim() });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('funds.settings.receiptSms.editOverride') : t('funds.settings.receiptSms.addOverride')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="overflow-visible" contentClassName="flex flex-col gap-4">
          <FormField label={t('funds.settings.receiptSms.overrideFund')}>
            {({ id }) => (
              <FundSelect
                id={id}
                value={fundId || null}
                onChange={(next) => setFundId(next ?? '')}
                disabled={disabled}
                popoverPortal={popoverPortal}
              />
            )}
          </FormField>
          <FormField label={t('funds.settings.receiptSms.overrideMessage')}>
            {({ id }) => (
              <>
                <Textarea
                  ref={bodyRef}
                  id={id}
                  value={body}
                  disabled={disabled}
                  onChange={(event) => setBody(event.target.value)}
                  onSelect={rememberSelection}
                  onKeyUp={rememberSelection}
                  onClick={rememberSelection}
                  onBlur={rememberSelection}
                  rows={4}
                />
                <MergeFieldInserter
                  onInsert={insertIntoBody}
                  includeGivingFields
                  disabled={disabled}
                />
              </>
            )}
          </FormField>
        </DialogBody>
        <DialogFooter>
          {isEditing && onDelete ? (
            <Button
              type="button"
              variant="destructive"
              className="mr-auto"
              disabled={disabled}
              onClick={() => {
                onDelete();
                onOpenChange(false);
              }}
            >
              {t('funds.settings.receiptSms.removeOverride')}
            </Button>
          ) : null}
          <Button type="button" variant="outline" disabled={disabled} onClick={() => onOpenChange(false)}>
            {tCommon('action.cancel')}
          </Button>
          <Button type="button" disabled={disabled || !canSave} onClick={handleSave}>
            {tCommon('action.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
